import { useArticlesData, useLotsData } from '@gozoki/api'
import type { Lot } from '@gozoki/api-types'
import {
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import type { GridColDef } from '@mui/x-data-grid'
import { createFileRoute } from '@tanstack/react-router'
import { Info } from 'lucide-react'
import { useState } from 'react'
import z from 'zod/v3'
import RbacAccessDeniedPage from '~/components/communication/RbacAccessDeniedPage'
import { Can } from '~/utils/ability'
import SimpleDataGrid from '../../../components/containers/SimpleDataGrid'
import Page from '../../../components/Page'
import { formatDateTime } from '../../../components/routes/quality/qualityUtils'
import { CustomFourDatePicker } from '../../../utils/dashboard/customFourDatePicker'
import { StoresPicker } from '../../../utils/dashboard/storesPicker'
import { ThemeColors } from '../../../utils/theme/colors.theme'
import { fonts } from '../../../utils/theme/fonts.theme'
import { formatDateOnly } from '../../../utils/types/formatDates'
const tabs = ['lots', 'articles'] as const
const statuses = ['all', 'blocked', 'unblocked'] as const

export const Route = createFileRoute('/_authenticated/quality/')({
  component: () => (
    <Can I="read" a="quality" passThrough>
      {({ isAllowed }) => (isAllowed ? <QualityPage /> : <RbacAccessDeniedPage />)}
    </Can>
  ),
  validateSearch: z.object({
    articleRef: z.string().optional(),
    dateEndDlc: z.coerce.date().optional(),
    dateEndSold: z.coerce.date().optional(),
    dateStartDlc: z.coerce.date().optional(),
    dateStartSold: z.coerce.date().optional(),
    ean: z.string().optional(),
    label: z.string().optional(),
    lotId: z.string().optional(),
    page: z.number().optional(),
    pageSize: z.number().optional(),
    status: z.enum(statuses).optional(),
    storeId: z.number().optional(),
    tab: z.enum(tabs).optional(),
  }),
})

const LotStatus = ({ lot }: { lot: Lot }) => {
  let title = 'Non bloqué'
  if (lot.blocked) {
    title = 'Bloqué'
  }
  if (!lot.blocked && lot.blockedAt) {
    title = 'Débloqué'
  }
  return <Chip style={{ height: 35 }} label={title} color={lot.blocked ? 'error' : 'success'} />
}

const qualityColumnDef = (): GridColDef[] => [
  {
    field: 'lot',
    headerName: 'N° de lot',
    width: 200,
  },
  {
    field: 'ean',
    headerName: 'EAN 13',
    width: 180,
  },
  { field: 'nb', headerName: "Nb d'articles", width: 100 },
  {
    field: 'label',
    headerName: 'Libellé',
    width: 350,
  },
  {
    field: 'dlc',
    headerName: 'DLC',
    valueFormatter: (params) => formatDateOnly(params.value as string),
    width: 180,
  },
  {
    field: 'tracking',
    headerName: 'Infos de traçabilité',
    width: 200,
  },
  {
    field: 'lo',
    headerName: 'Statut',
    renderCell: (params) => <LotStatus lot={params.row.lotDetail as Lot} />,
    width: 150,
  },
]

const articlesColumnDef = (): GridColDef[] => [
  {
    field: 'checkpointArticleRef',
    headerName: 'Référence',
    width: 220,
  },
  {
    field: 'ean',
    headerName: 'EAN 13',
    width: 180,
  },
  {
    field: 'label',
    headerName: 'Libellé',
    width: 300,
  },
  {
    field: 'dlc',
    headerName: 'DLC',
    valueFormatter: (params) => formatDateOnly(params.value as string),
    width: 140,
  },
  {
    field: 'storeLabel',
    headerName: 'Boutique',
    width: 150,
  },
  {
    field: 'warehouseAt',
    headerName: 'Plateforme',
    valueFormatter: (params) => formatDateTime(params.value as string),
    width: 160,
  },
  {
    field: 'shippingAt',
    headerName: 'Transport',
    valueFormatter: (params) => formatDateTime(params.value as string),
    width: 160,
  },
  {
    field: 'boutiqueAt',
    headerName: 'Boutique',
    valueFormatter: (params) => formatDateTime(params.value as string),
    width: 160,
  },
  {
    field: 'soldAt',
    headerName: 'Vendu',
    valueFormatter: (params) => formatDateTime(params.value as string),
    width: 160,
  },
  {
    field: 'stolenAt',
    headerName: 'Volé',
    valueFormatter: (params) => formatDateTime(params.value as string),
    width: 160,
  },
  {
    field: 'destroyedAt',
    headerName: 'Détruit',
    valueFormatter: (params) => formatDateTime(params.value as string),
    width: 160,
  },
]

function QualityPage() {
  const navigate = Route.useNavigate()
  const {
    page = 0,
    status = 'all',
    tab = 'lots',
    pageSize = 20,
    lotId = null,
    ean = null,
    label = null,
    articleRef = null,
    dateEndDlc,
    dateEndSold,
    dateStartDlc,
    dateStartSold,
    storeId = null,
  } = Route.useSearch()

  const [openDateDialogDlc, setOpenDateDialogDlc] = useState(false)
  const [openDateDialogSold, setOpenDateDialogSold] = useState(false)

  const {
    data: lots,
    isLoading: isLoadingLots,
    isFetching: isFetchingLots,
  } = useLotsData(
    page + 1, // X-data-grid starts at 0 but lucid starts at 1
    pageSize,
    lotId,
    ean,
    label,
    storeId,
    false,
    status,
    dateStartSold,
    dateEndSold,
    dateStartDlc,
    dateEndDlc,
  )

  const {
    data: articles,
    isLoading: isLoadingArticles,
    isFetching: isFetchingArticles,
  } = useArticlesData(
    page + 1,
    pageSize,
    articleRef,
    ean,
    label,
    storeId,
    dateStartSold,
    dateEndSold,
    dateStartDlc,
    dateEndDlc,
  )

  const safeLots = lots?.data ?? []
  const safeArticles = articles?.data ?? []

  const handleLotClick = (lot: Lot) => {
    navigate({ params: { lot: serializeLot(lot) }, to: '/quality/$lot' })
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const newTab = tabs.at(newValue) ?? 'lots'
    navigate({ search: { tab: newTab } })
  }

  return (
    <Page>
      <div style={{ display: 'flex', marginBottom: 10, paddingLeft: 10 }}>
        <Typography style={fonts.pageTitle} flexGrow={1}>
          Traçabilité & Qualité
        </Typography>
      </div>

      <Tabs value={tabs.indexOf(tab)} onChange={handleTabChange}>
        <Tab label="Recherche par lot" />
        <Tab label="Recherche par article" />
      </Tabs>

      <Paper
        sx={{
          marginTop: '16px',
        }}
      >
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            flex: 3,
            flexWrap: 'wrap',
            padding: '16px',
          }}
        >
          {tab === 'lots' && (
            <div style={{ padding: 16 }}>
              <TextField
                variant="outlined"
                label="Recherche par n° de lot"
                value={lotId}
                onChange={(e) =>
                  navigate({
                    search: (prev) => ({ ...prev, lotId: e.target.value }),
                  })
                }
              />
            </div>
          )}

          {tab === 'articles' && (
            <div style={{ padding: 16 }}>
              <TextField
                variant="outlined"
                label="Recherche par référence"
                value={articleRef}
                onChange={(e) =>
                  navigate({
                    search: (prev) => ({ ...prev, articleRef: e.target.value }),
                  })
                }
              />
            </div>
          )}

          <div style={{ padding: 16 }}>
            <TextField
              variant="outlined"
              label="Recherche par EAN 13"
              value={ean}
              onChange={(e) => navigate({ search: (prev) => ({ ...prev, ean: e.target.value }) })}
            />
          </div>
          <div style={{ padding: 16 }}>
            <TextField
              variant="outlined"
              label="Recherche par libellé"
              value={label}
              onChange={(e) => navigate({ search: (prev) => ({ ...prev, label: e.target.value }) })}
            />
          </div>
          <div style={{ alignItems: 'center', display: 'flex', gap: 8, padding: 16 }}>
            <Button
              variant="contained"
              onClick={() => setOpenDateDialogSold(true)}
              style={{
                backgroundColor:
                  dateStartSold && dateEndSold ? ThemeColors.LIGHT_ORANGE : ThemeColors.WHITE,
                color: dateStartSold && dateEndSold ? ThemeColors.ORANGE : ThemeColors.GREY_900,
                height: 55,
              }}
            >
              {dateStartSold && dateEndSold
                ? `${formatDateOnly(dateStartSold)} - ${formatDateOnly(dateEndSold)}`
                : 'Date de vente'}
            </Button>

            {tab === 'lots' && (
              <Tooltip
                title="Filtrer les lots par la date de vente des articles (lost_at). Seuls les lots contenant au moins un article vendu dans la période sélectionnée seront affichés."
                arrow
                placement="top"
              >
                <Info
                  style={{
                    color: ThemeColors.GREY_900,
                    cursor: 'help',
                    fontSize: 20,
                  }}
                />
              </Tooltip>
            )}

            {dateStartSold && dateEndSold && (
              <Button
                onClick={() => {
                  navigate({
                    search: (prev) => ({
                      ...prev,
                      dateStartSold: undefined,
                      dateEndSold: undefined,
                    }),
                  })
                }}
                style={{ marginLeft: 8 }}
              >
                Réinitialiser
              </Button>
            )}
          </div>
          <div style={{ alignItems: 'center', display: 'flex', gap: 8, padding: 16 }}>
            <Button
              variant="contained"
              onClick={() => setOpenDateDialogDlc(true)}
              style={{
                backgroundColor:
                  dateStartDlc && dateEndDlc ? ThemeColors.LIGHT_ORANGE : ThemeColors.WHITE,
                color: dateStartDlc && dateEndDlc ? ThemeColors.ORANGE : ThemeColors.GREY_900,
                height: 55,
              }}
            >
              {dateStartDlc && dateEndDlc
                ? `${formatDateOnly(dateStartDlc)} - ${formatDateOnly(dateEndDlc)}`
                : 'DLC'}
            </Button>

            {tab === 'lots' && (
              <Tooltip
                title="Filtrer les lots par la date limite de consommation (DLC) des articles. Seuls les lots contenant au moins un article dont la DLC est dans la période sélectionnée seront affichés."
                arrow
                placement="top"
              >
                <Info
                  style={{
                    color: ThemeColors.GREY_900,
                    cursor: 'help',
                    fontSize: 20,
                  }}
                />
              </Tooltip>
            )}

            {dateStartDlc && dateEndDlc && (
              <Button
                onClick={() => {
                  navigate({
                    search: (prev) => ({
                      ...prev,
                      dateStartDlc: undefined,
                      dateEndDlc: undefined,
                    }),
                  })
                }}
                style={{ marginLeft: 8 }}
              >
                Réinitialiser
              </Button>
            )}
          </div>

          {tab === 'lots' && (
            <div style={{ minWidth: 200, padding: 16 }}>
              <FormControl fullWidth>
                <InputLabel>Statut</InputLabel>
                <Select
                  label="Statut"
                  value={status}
                  onChange={(e) =>
                    navigate({
                      search: (prev) => ({
                        ...prev,
                        status: e.target.value as (typeof statuses)[number],
                      }),
                    })
                  }
                >
                  <MenuItem value={statuses[0]}>Tous</MenuItem>
                  <MenuItem value={statuses[1]}>Bloqué</MenuItem>
                  <MenuItem value={statuses[2]}>Non bloqué</MenuItem>
                </Select>
              </FormControl>
            </div>
          )}

          <div style={{ flex: 1, minWidth: 200 }}>
            <StoresPicker
              setUniqueStore={(newStoreId) =>
                navigate({ search: (prev) => ({ ...prev, storeId: newStoreId }) })
              }
              allowAllStores
              onlyBoutiques
              defaultStore={storeId ?? undefined}
            />
          </div>
        </div>
      </Paper>

      <CustomFourDatePicker
        openDialog={openDateDialogDlc}
        setOpenDialog={setOpenDateDialogDlc}
        dateState={{
          currentDate: {
            start: dateStartDlc ?? new Date(),
            setStart: (d) => navigate({ search: (prev) => ({ ...prev, dateStartDlc: d }) }),
            end: dateEndDlc ?? new Date(),
            setEnd: (d) => navigate({ search: (prev) => ({ ...prev, dateEndDlc: d }) }),
          },
          timeRanges: {
            all: [],
            current: 0,
            set: () => {},
          },
        }}
      />

      <CustomFourDatePicker
        openDialog={openDateDialogSold}
        setOpenDialog={setOpenDateDialogSold}
        dateState={{
          currentDate: {
            start: dateStartSold ?? new Date(),
            setStart: (d) => navigate({ search: (prev) => ({ ...prev, dateStartSold: d }) }),
            end: dateEndSold ?? new Date(),
            setEnd: (d) => navigate({ search: (prev) => ({ ...prev, dateEndSold: d }) }),
          },
          timeRanges: {
            all: [],
            current: 0,
            set: () => {},
          },
        }}
      />

      <Paper
        sx={{
          marginTop: '16px',
        }}
      >
        {tab === 'lots' ? (
          <SimpleDataGrid
            columns={qualityColumnDef()}
            rows={safeLots.map((l) => ({
              id: `${page}-${l.lot}-${l.ean}-${l.dlc}-${l.comment}-${l.tracking}-${l.blocked}`,
              lot: l.lot,
              ean: l.ean,
              nb: l.nb,
              label: l.label,
              dlc: l.dlc,
              tracking: l.tracking,
              lotDetail: l,
            }))}
            loading={isLoadingLots || isFetchingLots}
            rowCount={lots?.meta.total ?? 0}
            pageSizeOptions={[20, 50]}
            paginationMode="server"
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(params) => {
              navigate({
                search: (prev) => ({
                  ...prev,
                  page: params.page,
                  pageSize: params.pageSize,
                }),
              })
            }}
            disableRowSelectionOnClick
            checkboxSelection={false}
            onRowClick={(row) => {
              if (row.row.lotDetail) {
                handleLotClick(row.row.lotDetail as Lot)
              }
            }}
          />
        ) : (
          <SimpleDataGrid
            columns={articlesColumnDef()}
            rows={safeArticles.map((a) => ({
              id: a.checkpointArticleRef,
              checkpointArticleRef: a.checkpointArticleRef,
              ean: a.ean,
              label: a.label,
              dlc: a.dlc,
              storeLabel: a.storeLabel,
              warehouseAt: a.warehouseAt,
              shippingAt: a.shippingAt,
              boutiqueAt: a.boutiqueAt,
              soldAt: a.soldAt,
              stolenAt: a.stolenAt,
              destroyedAt: a.destroyedAt,
            }))}
            loading={isLoadingArticles || isFetchingArticles}
            rowCount={articles?.meta.total ?? 0}
            pageSizeOptions={[20, 50]}
            paginationMode="server"
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(params) => {
              navigate({
                search: (prev) => ({
                  ...prev,
                  page: params.page,
                  pageSize: params.pageSize,
                }),
              })
            }}
            disableRowSelectionOnClick
            checkboxSelection={false}
          />
        )}
      </Paper>
    </Page>
  )
}

function serializeLot(lot: Lot) {
  const lotData = JSON.stringify({
    dlc: lot.dlc,
    ean: lot.ean,
    lotNumber: lot.lot,
    tracking: lot.tracking,
  })

  const bytes = new TextEncoder().encode(lotData)
  const binaryString = String.fromCharCode(...bytes)

  // String -> base64 -> base64url
  return btoa(binaryString).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
